import React, { ReactElement, useContext, useMemo, useState } from "react";
import { iFixVersionManifest, iSiloManifest, useCmsContext } from "@rotorstudios/fe-cms";

import {
  iCmeSiloUi,
  iCmsDynamicVariable,
  iConfiguratorState,
  iDealer,
  iDealership,
  iEmailState,
  iFslDropdownState,
  iMaterialCode,
  iModalState,
  iPinData,
  iPricingType,
  iSidebarState,
  iUser,
  iVehicleData,
  iVehicleStock,
  GradeSpecificationsList,
} from "./types";
import { SIDEBAR_CONTENT, EMAIL_VIEW } from "./enums";
import { ACCESSORY_CATEGORY_EXTERIOR, CASH, MAIN_CATEGORY_VEHICLE } from "./static";
import useAppLoadingData from "./useAppLoadingData";
import useCmeSiloUiData from "./useCmeSiloUiData";
import useVehicleStockData from "./useVehicleStockData";
import useDynamicVariables from "./useDynamicVariables";
import useVehicleCardsData from "./useVehicleCardsData";
import useHybridEngines from "./useHybridEngines";
import useKillSwitch from "./useKillSwitch";
import type { IVehicleCard } from "../../uiModules/uiMVehicleSelector/components/VehicleCard";
import type { IVehicleFilter } from "../../uiModules/uiMVehicleSelector/components/VehicleFilter";
import useFeaturedAccessories from "./useFeaturedAccessories";

type iDataContext = {
  errorState: boolean;
  isLoading: boolean;
  isInitialDataValidated: boolean;
  loadingSequence?: string;
  modalState: iModalState;
  sidebarState: iSidebarState;
  emailState: iEmailState;
  fslDropdownState: iFslDropdownState;
  urlParams: any;
  pinData: iPinData;
  vehicleData: iVehicleData;
  dealershipData: iDealership;
  dealerData: iDealer;
  userData: iUser;
  vehicleCards: IVehicleCard[];
  vehicleFilters: IVehicleFilter[];
  cmeSiloUiData: iCmeSiloUi;
  fixVersionManifest: iFixVersionManifest;
  siloManifest: iSiloManifest;
  dynamicVariables: Map<string, iCmsDynamicVariable>;
  vehicleStockData: iVehicleStock;
  currentPricingType: iPricingType;
  configuratorState: iConfiguratorState;
  materialCode?: string;
  isHybridEngine: string;
  gradeSpecificationsList?: GradeSpecificationsList;
  getCmeSiloOptions: (name: string, feature: string) => any;
  isFeatureDisabled: (id: string) => boolean;
  updateErrorState: (state: boolean) => void;
  updateIsLoading: (state: boolean) => void;
  updateLoadingSequence: (state: string) => void;
  updateSidebarState: (state: iSidebarState) => void;
  updateEmailState: (state: iEmailState) => void;
  updateFslDropdownState: (state: iFslDropdownState) => void;
  updateCurrentPricingType: (state: iPricingType) => void;
  updateConfiguratorState: (state: iConfiguratorState) => void;
  updatePinData: (state: iPinData) => void;
  updateUserName: (name?: string) => void;
  updateVehicle: (vehicleId: string) => void;
  updateMaterialCode: (state: iMaterialCode) => void;
  updateVehicleEngine: (id: string) => void;
  updateGradeSpecificationsList: (list: GradeSpecificationsList) => void;
};

export const DataContext = React.createContext<iDataContext | null | any>(null);
export const useDataContext = () => {
  const context = useContext(DataContext);

  if (!context) {
    throw new Error("useDataContext must be used within a DataProvider");
  }

  return context;
};

type iProps = {
  children: React.ReactNode;
};

export const DataProvider = ({ children }: iProps): ReactElement => {
  const { fixVersionManifest, siloManifest, cmsErrorState } = useCmsContext();

  // ********************
  // Local State
  // ********************
  const [errorState, setErrorState] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingSequence, setLoadingSequence] = useState<string>("");
  const [modalState, setModalState] = useState<iModalState>({
    id: "",
    data: null,
    isVisible: false,
  });
  const [sidebarState, setSidebarState] = useState<iSidebarState>(
    SIDEBAR_CONTENT.DEFAULT,
  );
  const [emailState, setEmailState] = useState<iEmailState>(EMAIL_VIEW.INITIAL);
  const [fslDropdownState, setFslDropdownState] = useState<iFslDropdownState>({
    distance: "15000",
    leasePeriod: "3",
  });
  const [currentPricingType, setCurrentPricingType] = useState<iPricingType>(CASH);
  const [configuratorState, setConfiguratorState] = useState<iConfiguratorState>({
    mainCategory: MAIN_CATEGORY_VEHICLE,
    accessoryCategoryId: ACCESSORY_CATEGORY_EXTERIOR,
    accessoryCategoryTitle: ACCESSORY_CATEGORY_EXTERIOR,
  });
  const [materialCode, setMaterialCode] = useState<iMaterialCode>({
    config: "",
    pricing: "",
  });
  const [vehicleEngine, setVehicleEngine] = useState<string>("");
  const [isQtvrOverlayVisible, setIsQtvrOverlayVisible] = useState(true);
  const [gradeSpecificationsList, setGradeSpecificationsList] = useState<
    GradeSpecificationsList | undefined
  >();

  // ********************
  // Initial App Data (vehicle, dealership, init config, user data)
  // ********************
  const {
    isInitialDataValidated,
    urlParams,
    pinData,
    vehicleData,
    dealershipData,
    dealerData,
    userData,
    updatePinData,
    updateUserName,
    updateVehicle,
  } = useAppLoadingData();

  // ********************
  // CMS CME Silo UI Data
  // ********************
  const { cmeSiloUiData, getCmeSiloUiOptions } = useCmeSiloUiData();

  // ********************
  // CMS Vehicle Cards and Filters Data
  // ********************
  const { vehicleCards, vehicleFilters } = useVehicleCardsData();

  // ********************
  // CMS Dynamic Variables
  // ********************
  const { dynamicVariables } = useDynamicVariables();

  // ********************
  // CMS Hybrid Engines
  // ********************
  const { isHybridEngine } = useHybridEngines({ vehicleEngine });

  // ********************
  // CMS Kill Switch Data and QA disable feature flag
  // ********************
  const { isFeatureDisabled } = useKillSwitch();

  // ********************
  // API Cash Pricing Data
  // ********************
  const { vehicleStockData } = useVehicleStockData({
    setErrorState,
    stockDealerId: dealershipData?.stockDealerId,
    materialCode: materialCode.pricing,
  });

  // ********************
  // CMS Accessories Hero Data
  // ********************
  const { featuredAccessoriesData } = useFeaturedAccessories();

  // *******************
  // State update methods
  // *******************
  const updateErrorState = (state: boolean) => setErrorState(state);

  const updateIsLoading = (state: boolean) => setIsLoading(state);

  const updateLoadingSequence = (state: string) => setLoadingSequence(state);

  const updateMaterialCode = (state: iMaterialCode) => setMaterialCode(state);

  const updateVehicleEngine = (id: string) => setVehicleEngine(id);

  const updateModalState = (state: iModalState) => setModalState(state);

  const updateSidebarState = (state: iSidebarState) => setSidebarState(state);

  const updateEmailState = (state: iEmailState) => setEmailState(state);

  const updateFslDropdownState = (state: iFslDropdownState) =>
    setFslDropdownState((prevState: iFslDropdownState) => ({ ...prevState, ...state }));

  const updateCurrentPricingType = (state: iPricingType) => setCurrentPricingType(state);

  const updateConfiguratorState = (state: iConfiguratorState) =>
    setConfiguratorState((prevState: iConfiguratorState) => ({ ...prevState, ...state }));

  const updateIsQtvrOverlayVisible = (state: boolean) => setIsQtvrOverlayVisible(state);

  const updateGradeSpecificationsList = (list: GradeSpecificationsList) =>
    setGradeSpecificationsList(list);

  const shouldRender = useMemo(
    () => Boolean(fixVersionManifest || errorState || cmsErrorState),
    [fixVersionManifest, errorState, cmsErrorState],
  );

  const providerValue = useMemo(
    () => ({
      errorState,
      isLoading,
      isInitialDataValidated,
      loadingSequence,
      modalState,
      sidebarState,
      emailState,
      fslDropdownState,
      urlParams,
      pinData,
      vehicleData,
      dealershipData,
      dealerData,
      userData,
      vehicleCards,
      vehicleFilters,
      cmeSiloUiData,
      fixVersionManifest,
      siloManifest,
      dynamicVariables,
      vehicleStockData,
      currentPricingType,
      configuratorState,
      materialCode,
      isHybridEngine,
      getCmeSiloUiOptions,
      isFeatureDisabled,
      isQtvrOverlayVisible,
      gradeSpecificationsList,
      featuredAccessoriesData,
      //
      updateErrorState,
      updateIsLoading,
      updateLoadingSequence,
      updateModalState,
      updateSidebarState,
      updateEmailState,
      updateFslDropdownState,
      updateCurrentPricingType,
      updateConfiguratorState,
      updatePinData,
      updateUserName,
      updateVehicle,
      updateMaterialCode,
      updateVehicleEngine,
      updateIsQtvrOverlayVisible,
      updateGradeSpecificationsList,
    }),
    [
      cmeSiloUiData,
      configuratorState,
      currentPricingType,
      dealerData,
      dealershipData,
      dynamicVariables,
      emailState,
      errorState,
      featuredAccessoriesData,
      fixVersionManifest,
      fslDropdownState,
      getCmeSiloUiOptions,
      isFeatureDisabled,
      isHybridEngine,
      isInitialDataValidated,
      isLoading,
      isQtvrOverlayVisible,
      gradeSpecificationsList,
      loadingSequence,
      materialCode,
      modalState,
      pinData,
      sidebarState,
      siloManifest,
      updatePinData,
      updateUserName,
      updateVehicle,
      urlParams,
      userData,
      vehicleCards,
      vehicleData,
      vehicleFilters,
      vehicleStockData,
    ],
  );

  return (
    <DataContext.Provider value={providerValue}>
      {shouldRender && children}
    </DataContext.Provider>
  );
};
